import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../context/ThemeContext';
import './Navbar.css';

const Navbar: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const handleLanguageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    i18n.changeLanguage(event.target.value);
  };

  const supportedLanguages = [
    { code: 'en', name: 'English' },
    { code: 'hr', name: 'Hrvatski' },
  ];

  const navLinks = [
    { to: '/about', label: t('navbar.aboutUs') },
    { to: '/images', label: t('navbar.images') },
    { to: '/prices', label: t('navbar.prices') },
    { to: '/booking', label: t('navbar.booking') },
    { to: '/contact', label: t('navbar.contact') },
  ];

  return (
    <nav className={`navbar ${mobileMenuOpen ? 'navbar-menu-open' : 'glass'}`}>
      <div className="navbar-container container">
        <NavLink to="/" className="navbar-brand">{t('navbar.brand')}</NavLink>
        
        {/* Desktop links */}
        <ul className="navbar-links">
          {navLinks.map(link => (
            <li key={link.to}>
              <NavLink to={link.to} className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}>
                {link.label}
              </NavLink>
            </li>
          ))}
        </ul>

        <div className="controls-container">
          <div className="language-switcher desktop-only">
            <select onChange={handleLanguageChange} value={i18n.language.split('-')[0]} className="language-select">
              {supportedLanguages.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.name}
                </option>
              ))}
            </select>
          </div>
          
          {/* Hamburger button — mobile only */}
          <button
            className="hamburger-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
            aria-expanded={mobileMenuOpen}
          >
            <span className="material-symbols-outlined material-symbols-rounded">
              {mobileMenuOpen ? 'close' : 'menu'}
            </span>
          </button>
        </div>
      </div>

      {/* Mobile menu overlay */}
      <div className={`mobile-menu-overlay ${mobileMenuOpen ? 'open' : ''}`}>
        <ul className="mobile-menu-links">
          {navLinks.map(link => (
            <li key={link.to}>
              <NavLink
                to={link.to}
                className={({ isActive }) => isActive ? "mobile-nav-link active" : "mobile-nav-link"}
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.label}
              </NavLink>
            </li>
          ))}
        </ul>
        <div className="mobile-menu-footer">
          <select onChange={handleLanguageChange} value={i18n.language.split('-')[0]} className="language-select mobile-language-select">
            {supportedLanguages.map((lang) => (
              <option key={lang.code} value={lang.code}>
                {lang.name}
              </option>
            ))}
          </select>
        </div>
      </div>
    </nav>
  );
};

export default Navbar; 